/**
 * Engraved shapes: noteheads, rests, clefs, accidentals and ledger lines.
 *
 * Hand-rolled rather than pulled from a notation library. First species is whole
 * notes on a staff, which is trivial to draw, and owning the SVG is what makes
 * ghost noteheads, column highlights and click-to-place straightforward instead
 * of a fight with somebody else's DOM.
 */

import type { JSX } from 'react';
import { ledgerLines, SPACE, STEP, yOf, type Staff } from './layout';

/** A whole note: an open oval with the traditional slanted counter. */
export function WholeNote({
  x,
  d,
  staff,
  color,
  opacity = 1,
  hollow = 'var(--paper)',
}: {
  x: number;
  d: number;
  staff: Staff;
  color: string;
  opacity?: number;
  hollow?: string;
}): JSX.Element {
  const y = yOf(d, staff);
  return (
    <g opacity={opacity} pointerEvents="none">
      {ledgerLines(d, staff).map((ly, i) => (
        <line key={i} x1={x - 11} y1={ly} x2={x + 11} y2={ly} stroke={color} strokeWidth={1.2} />
      ))}
      <ellipse cx={x} cy={y} rx={7} ry={4.8} fill={color} />
      <ellipse cx={x} cy={y} rx={3.6} ry={2.5} fill={hollow} transform={`rotate(-28 ${x} ${y})`} />
    </g>
  );
}

/**
 * A semibreve rest: a filled block hanging from the second line down.
 * Every measure of the counterpoint starts as one of these.
 */
export function WholeRest({
  x,
  d,
  staff,
  color,
  opacity = 1,
}: {
  x: number;
  /** The staff line the rest hangs from — chosen by the layout. */
  d: number;
  staff: Staff;
  color: string;
  opacity?: number;
}): JSX.Element {
  return (
    <rect
      x={x - 8}
      y={yOf(d, staff)}
      width={16}
      height={SPACE * 0.5}
      fill={color}
      opacity={opacity}
      pointerEvents="none"
      rx={0.5}
    />
  );
}

/** A treble or bass clef in Petaluma's hand-copied notation style. */
export function Clef({ staff, color }: { staff: Staff; color: string }): JSX.Element {
  const treble = staff.clef === 'treble';
  // SMuFL registers each clef's named pitch on the font baseline: treble G is
  // the second line from the bottom, and bass F the second line from the top.
  const baseline = staff.top + (treble ? 3 * SPACE : SPACE);
  // Petaluma's broad copyist strokes are optically balanced against the
  // five-line staff at separate treble and bass sizes.
  const fontSize = treble ? 44 : 46;
  return (
    <text
      className="score__clef"
      x={treble ? 3 : 4}
      y={baseline}
      fontSize={fontSize}
      fill={color}
      pointerEvents="none"
    >
      {treble ? '\uE050' : '\uE062'}
    </text>
  );
}

/** One accidental glyph placed to the left of a notehead. */
export function Accidental({
  x,
  d,
  staff,
  glyph,
  color,
  opacity = 1,
}: {
  x: number;
  d: number;
  staff: Staff;
  glyph: string;
  color: string;
  opacity?: number;
}): JSX.Element {
  return (
    <text
      x={x - 20}
      y={yOf(d, staff) + 5.5}
      fontSize={19}
      fill={color}
      opacity={opacity}
      fontFamily="serif"
      pointerEvents="none"
    >
      {glyph}
    </text>
  );
}

/** The five lines of one staff. */
export function StaffLines({
  staff,
  from,
  to,
  color,
}: {
  staff: Staff;
  from: number;
  to: number;
  color: string;
}): JSX.Element {
  return (
    <g pointerEvents="none">
      {[0, 1, 2, 3, 4].map((l) => (
        <line
          key={l}
          x1={from}
          y1={staff.top + l * SPACE}
          x2={to}
          y2={staff.top + l * SPACE}
          stroke={color}
          strokeWidth={1}
        />
      ))}
    </g>
  );
}

/** A caret marking where the keyboard cursor sits on the staff. */
export function PitchCaret({
  x,
  d,
  staff,
  color,
}: {
  x: number;
  d: number;
  staff: Staff;
  color: string;
}): JSX.Element {
  const y = yOf(d, staff);
  return (
    <g pointerEvents="none" stroke={color} strokeWidth={1.6} strokeLinecap="round" fill="none">
      <path d={`M ${x - 14} ${y - STEP} l -4 0 l 0 ${STEP * 2} l 4 0`} />
      <path d={`M ${x + 14} ${y - STEP} l 4 0 l 0 ${STEP * 2} l -4 0`} />
    </g>
  );
}
